import { NewPractitionerConsumption } from '../../../../models/Entities/Practitioners/NewPractitionerConsumption';
import { ProtocolConsumptionRealizeTypeEnum } from '../../../../models/Enums/ProtocolConsumptionRealizeTypeEnum';

/**
 * Armado del alta manual de un consumo del médico, sin React. Lo que exige la
 * API (PractitionerConsumptionCreateCommandAsync y su validador):
 *   - instanceId no vacío, currencyCode no vacío, quantity entero > 0,
 *     unitAmount y amount ≥ 0.
 *   - los ids opcionales (concepto, etapa, EMR) van en null, no en 0.
 *   - stageInternalCrossVersionId es un uuid: vacío rompe el binding.
 * El servidor fija solo status, origin, rateType y las monedas originales.
 */

export interface ConsumptionDraft {
    practitionerId: string;
    practitionerName: string;
    protocolId: number;
    protocolName: string;
    contractId?: number;
    addendumId?: number;
    contractConceptId?: number;
    conceptName?: string;
    stageId?: number;
    stageName?: string;
    stageInternalCrossVersionId?: string;
    patientNumberInProtocol?: string;
    serviceName: string;
    serviceDueDate: Date;
    realizedType: number;
    currencyCode: string;
    quantity: number;
    unitAmount: number;
    comments: string;
}

export interface ConsumptionDraftErrors {
    protocol?: string;
    service?: string;
    currency?: string;
    quantity?: string;
    unitAmount?: string;
    dueDate?: string;
}

/** Cantidad: entero. Importe: decimal con coma o punto. NaN cuando no es número. */
export const parseInteger = (text: string): number => {
    const trimmed = (text ?? '').trim();
    if (!trimmed) return Number.NaN;
    return /^\d+$/.test(trimmed) ? Number(trimmed) : Number.NaN;
};

export const parseDecimal = (text: string): number => {
    const trimmed = (text ?? '').trim();
    if (!trimmed) return Number.NaN;
    return Number(trimmed.replace(',', '.'));
};

/** Sólo dígitos. */
export const sanitizeIntegerText = (value: string): string => (value ?? '').replace(/\D/g, '');

/** Dígitos y un único separador decimal. */
export const sanitizeDecimalText = (value: string): string => {
    const cleaned = (value ?? '').replace(/[^0-9.,]/g, '');
    const separatorIndex = cleaned.search(/[.,]/);
    if (separatorIndex < 0) return cleaned;
    return cleaned.slice(0, separatorIndex + 1) + cleaned.slice(separatorIndex + 1).replace(/[.,]/g, '');
};

export const validateDraft = (input: {
    protocolId?: number;
    conceptSelected: boolean;
    serviceName: string;
    currencyCode: string;
    quantityText: string;
    unitAmountText: string;
    serviceDueDate?: Date;
}): ConsumptionDraftErrors => {
    const errors: ConsumptionDraftErrors = {};
    const quantity = parseInteger(input.quantityText);
    const unitAmount = parseDecimal(input.unitAmountText);

    if (!input.protocolId) errors.protocol = 'Seleccioná un protocolo.';
    if (!input.conceptSelected && !input.serviceName.trim()) errors.service = 'Indicá un concepto o el nombre del servicio.';
    if (!input.currencyCode) errors.currency = 'Seleccioná una moneda.';
    if (!Number.isInteger(quantity) || quantity <= 0) errors.quantity = 'La cantidad es un entero mayor a cero.';
    if (!Number.isFinite(unitAmount) || unitAmount < 0) errors.unitAmount = 'El valor unitario es un importe mayor o igual a cero.';
    if (!input.serviceDueDate) errors.dueDate = 'Indicá la fecha del servicio.';
    return errors;
};

export const hasDraftErrors = (errors: ConsumptionDraftErrors): boolean => Object.keys(errors).length > 0;

const round2 = (value: number): number => Math.round(value * 100) / 100;

/** instanceId lo exige el validador; se genera uno nuevo por alta. */
export const newInstanceId = (): string => {
    const api = (globalThis as any).crypto;
    if (api?.randomUUID) return api.randomUUID();
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = Math.random() * 16 | 0;
        return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });
};

/** Manual = 2 en ConsumptionOriginEnum; el servidor lo fija igual, pero el cuerpo no debe decir "sistema". */
const ORIGIN_MANUAL = 2;
/** Un consumo cargado a mano no está en el flowchart: Additional = 2 en ProtocolConsumptionTypeOfServiceEnum. */
const TYPE_OF_SERVICE_ADDITIONAL = 2;

export const buildConsumption = (draft: ConsumptionDraft, instanceId: string = newInstanceId()): NewPractitionerConsumption => {
    const amount = round2(draft.quantity * draft.unitAmount);
    const serviceName = draft.serviceName.trim() || draft.conceptName || '';
    return {
        practitionerId: draft.practitionerId,
        practitionerName: draft.practitionerName,
        currentPractitionerId: draft.practitionerId,
        currentPractitionerName: draft.practitionerName,
        protocolId: draft.protocolId,
        protocolName: draft.protocolName,
        contractId: draft.contractId ?? 0,
        addendumId: draft.addendumId ?? 0,
        contractConceptId: draft.contractConceptId ?? null,
        stageId: draft.stageId ?? null,
        stageName: draft.stageName || '',
        stageInternalCrossVersionId: draft.stageInternalCrossVersionId || null,
        emrCrossVersionId: null,
        settlementId: null,
        instanceId,
        parentInstanceId: '',
        patientNumberInProtocol: draft.patientNumberInProtocol || '',
        serviceName,
        serviceDueDate: draft.serviceDueDate.toISOString(),
        conceptDescription: draft.conceptName || serviceName,
        settlementIdentifier: '',
        settlementName: '',
        quantity: draft.quantity,
        coefficientApplied: 1,
        currencyCode: draft.currencyCode,
        unitAmount: round2(draft.unitAmount),
        amount,
        payeeAmount: 0,
        pendingAmount: amount,
        referenceUnitAmount: round2(draft.unitAmount),
        percentageApplied: 0,
        status: 0,
        typeOfService: TYPE_OF_SERVICE_ADDITIONAL,
        realizedType: draft.realizedType || ProtocolConsumptionRealizeTypeEnum.RealizedFaceToFace,
        originalConsumptionType: 0,
        currentConsumptionType: 0,
        origin: ORIGIN_MANUAL,
        markedToSettle: false,
        markedAsPayee: false,
        markedAsFlagged: false,
        markedAsItemized: false,
        markedAsRefundable: false,
        markedAsNeedMoreInfo: false,
        markedAsHaveWarnings: false,
        markedAsTrouble: false,
        comments: draft.comments.trim(),
        moreInfoComments: '',
        warningsComments: '',
        troubleComments: '',
        tag1: '',
        tag2: '',
        tag3: '',
        tag4: ''
    };
};
